import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Plus, Trash2, Calendar, Copy, HardHat, Star } from "lucide-react";
import MultiImageUpload from "@/components/MultiImageUpload";
import QuickAddSelect from "@/components/QuickAddSelect";
import MachineRunScheduleFields, { DEFAULT_RUN_SCHEDULE } from "@/components/MachineRunScheduleFields";

const empty = {
  name: "",
  category: "",
  serial: "",
  manufacturer: "",
  model: "",
  cost: "",
  purchased_at: "",
  warranty_until: "",
  department: "",
  status: "available",
  notes: "",
  service_interval_days: "",
  last_serviced_at: "",
  hours: "",
  has_hour_meter: false,
  run_schedule: DEFAULT_RUN_SCHEDULE,
  scheduled_services: [],
  spares: [],
  image_paths: [],
  tech_links: [],
};

const STATUSES = [
  ["available", "Available"],
  ["in_use", "In use"],
  ["needs_service_unconfirmed", "Needs service (unconfirmed)"],
  ["needs_service", "Needs service"],
  ["broken_unconfirmed", "Broken (unconfirmed)"],
  ["broken", "Broken"],
  ["retired", "Retired"],
];

/**
 * Manager-only — create or edit a machine.
 * Loads catalog stock items + assets once for the linked-spares picker.
 */
const MachineFormDialog = ({ open, onClose, editing, categories, departments, onSaved, onCategoriesChanged }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [spareKind, setSpareKind] = useState("stock");
  const [spareRef, setSpareRef] = useState("");
  const [spareLabel, setSpareLabel] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [schedNote, setSchedNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? {
            ...empty,
            ...editing,
            cost: editing.cost ?? "",
            service_interval_days: editing.service_interval_days ?? "",
            purchased_at: (editing.purchased_at || "").slice(0, 10),
            warranty_until: (editing.warranty_until || "").slice(0, 10),
            last_serviced_at: (editing.last_serviced_at || "").slice(0, 10),
            hours: editing.hours ?? "",
            has_hour_meter: !!editing.has_hour_meter,
            run_schedule: editing.run_schedule || DEFAULT_RUN_SCHEDULE,
            scheduled_services: editing.scheduled_services || [],
            spares: editing.spares || [],
            image_paths: editing.image_paths || [],
            tech_links: editing.tech_links || [],
          }
        : empty,
    );
    setSpareKind("stock");
    setSpareRef("");
    setSpareLabel("");
    setSchedDate("");
    setSchedNote("");
    setDuplicating(false);
    Promise.all([api.get("/stock"), api.get("/assets"), api.get("/technicians")])
      .then(([s, a, t]) => {
        setStockItems(s.data || []);
        setAssets(a.data || []);
        setTechnicians(t.data || []);
      })
      .catch(() => {});
  }, [open, editing]);

  const sparePool = useMemo(
    () => (spareKind === "stock" ? stockItems : assets),
    [spareKind, stockItems, assets],
  );

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSpare = () => {
    if (!spareRef) return;
    const pool = spareKind === "stock" ? stockItems : assets;
    const item = pool.find((x) => x.id === spareRef);
    const label = spareLabel.trim() || item?.name || "";
    setForm((f) => ({
      ...f,
      spares: [...f.spares, { kind: spareKind, ref_id: spareRef, label }],
    }));
    setSpareRef("");
    setSpareLabel("");
  };

  const removeSpare = (idx) =>
    setForm((f) => ({ ...f, spares: f.spares.filter((_, i) => i !== idx) }));

  const addScheduled = () => {
    if (!schedDate) return;
    setForm((f) => ({
      ...f,
      scheduled_services: [
        ...f.scheduled_services,
        { date: schedDate, note: schedNote.trim() || null },
      ],
    }));
    setSchedDate("");
    setSchedNote("");
  };

  const removeScheduled = (idx) =>
    setForm((f) => ({
      ...f,
      scheduled_services: f.scheduled_services.filter((_, i) => i !== idx),
    }));

  const lookupName = (s) => {
    const pool = s.kind === "stock" ? stockItems : assets;
    const it = pool.find((x) => x.id === s.ref_id);
    return s.label || it?.name || s.ref_id;
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        service_interval_days:
          form.service_interval_days === "" ? null : Number(form.service_interval_days),
        hours: form.hours === "" ? null : Number(form.hours),
        purchased_at: form.purchased_at || null,
        warranty_until: form.warranty_until || null,
        last_serviced_at: form.last_serviced_at || null,
        category: form.category || null,
        department: form.department || null,
      };
      // tech_links go via a dedicated endpoint, not the machine PUT body.
      delete payload.tech_links;
      // Service agreement snapshots are managed by the ServiceAgreement
      // reconciler — the machine form must NEVER write them, so strip them
      // here even though they get spread in from `editing`. Backend PUT is
      // whitelist-based, so this is a belt-and-braces defensive strip that
      // also guards against any future middleware picking them up.
      delete payload.service_agreement_id;
      delete payload.service_agreement_end;
      delete payload.service_agreement_supplier;
      const techLinksPayload = (form.tech_links || []).map((tl) => ({
        tech_id: tl.tech_id,
        is_primary: !!tl.is_primary,
        last_service_at: tl.last_service_at || null,
      }));
      let mid;
      if (editing && !duplicating) {
        await api.put(`/machines/${editing.id}`, payload);
        mid = editing.id;
        toast.success("Machine updated");
      } else {
        const { data } = await api.post("/machines", payload);
        mid = data?.id;
        toast.success(
          duplicating ? `Cloned as "${form.name}"` : `Added "${form.name}"`,
        );
      }
      if (mid) {
        try {
          await api.put(`/machines/${mid}/technicians`, {
            links: techLinksPayload,
          });
        } catch (te) {
          toast.error(
            te.response?.data?.detail || "Machine saved but tech links failed",
          );
        }
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const duplicate = () => {
    if (!editing) return;
    setForm((f) => ({
      ...f,
      // Blank out identity + per-instance fields so save() POSTs a new row.
      // Keep category, dept, spec, cost & spares — those are almost always
      // the same for a sibling machine (e.g. a second HP 4210 printer).
      name: f.name ? `${f.name.trim()} (copy)` : "",
      serial: "",
      hours: "",
      purchased_at: "",
      warranty_until: "",
      last_serviced_at: "",
      status: "available",
      notes: "",
    }));
    setDuplicating(true);
    toast.info("Duplicating — edit serial, purchase date and any other unique fields, then Save");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="machine-form-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(900px,95vw)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Machinery</div>
            <h3 className="font-display text-2xl">
              {editing && !duplicating
                ? "Edit machine"
                : duplicating
                  ? `Duplicating${form.name ? ` — ${form.name}` : ""}`
                  : "New machine"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {editing && !duplicating && (
              <button
                data-testid="machine-form-duplicate"
                onClick={duplicate}
                className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5"
                title="Clone this machine — same category/dept/spec, blank serial and dates"
              >
                <Copy size={14} strokeWidth={2.5} /> Duplicate
              </button>
            )}
            <button onClick={onClose} className="nb-btn bg-white p-2" data-testid="machine-form-close">
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="p-5 grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Name *</div>
            <input
              data-testid="machine-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Top-cover grinder #1"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Category</div>
            <QuickAddSelect
              testidPrefix="machine-category"
              value={form.category}
              onChange={(v) => set("category", v)}
              options={categories}
              endpoint="/machine-categories"
              placeholder="— None —"
              onCreated={() => onCategoriesChanged?.()}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Department</div>
            <select
              data-testid="machine-dept-select"
              className="nb-input"
              value={form.department}
              onChange={(e) => set("department", e.target.value)}
            >
              <option value="">— None —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Manufacturer</div>
            <input
              data-testid="machine-manufacturer-input"
              className="nb-input"
              value={form.manufacturer || ""}
              onChange={(e) => set("manufacturer", e.target.value)}
              placeholder="e.g. Hummel"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Model</div>
            <input
              data-testid="machine-model-input"
              className="nb-input"
              value={form.model || ""}
              onChange={(e) => set("model", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Serial / asset tag</div>
            <input
              data-testid="machine-serial-input"
              className="nb-input font-mono-num"
              value={form.serial || ""}
              onChange={(e) => set("serial", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Status</div>
            <select
              data-testid="machine-status-select"
              className="nb-input"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              {STATUSES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Cost ($)</div>
            <input
              data-testid="machine-cost-input"
              type="number"
              step="0.01"
              className="nb-input font-mono-num"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              placeholder="0.00"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Purchased on</div>
            <input
              data-testid="machine-purchased-input"
              type="date"
              className="nb-input"
              value={form.purchased_at || ""}
              onChange={(e) => set("purchased_at", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Warranty until</div>
            <input
              data-testid="machine-warranty-input"
              type="date"
              className="nb-input"
              value={form.warranty_until || ""}
              onChange={(e) => set("warranty_until", e.target.value)}
            />
          </label>

          {/* Service scheduling */}
          <div className="sm:col-span-2 border-2 border-black rounded-xl p-3 bg-[#F4F8FB]">
            <div className="label-eyebrow mb-2">Service scheduling</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                <div className="text-xs font-semibold mb-1">Interval (days)</div>
                <input
                  data-testid="machine-interval-input"
                  type="number"
                  min="0"
                  className="nb-input font-mono-num"
                  value={form.service_interval_days}
                  onChange={(e) => set("service_interval_days", e.target.value)}
                  placeholder="e.g. 90 for quarterly"
                />
              </label>
              <label>
                <div className="text-xs font-semibold mb-1">Last serviced on</div>
                <input
                  data-testid="machine-last-serviced-input"
                  type="date"
                  className="nb-input"
                  value={form.last_serviced_at || ""}
                  onChange={(e) => set("last_serviced_at", e.target.value)}
                />
              </label>
            </div>

            <div className="mt-3">
              <label>
                <div className="text-xs font-semibold mb-1">
                  {form.has_hour_meter ? "Current hour-meter reading" : "Current hours (starting point for estimates)"}
                </div>
                <input
                  data-testid="machine-hours-input"
                  type="number"
                  min="0"
                  step="any"
                  className="nb-input font-mono-num w-48"
                  value={form.hours}
                  onChange={(e) => set("hours", e.target.value)}
                  placeholder="e.g. 4582.5"
                />
                <div className="text-[10px] text-[#607087] mt-1">
                  Optional — used for service scheduling & hours reports.
                </div>
              </label>
              <MachineRunScheduleFields
                hasMeter={form.has_hour_meter}
                schedule={form.run_schedule}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              />
            </div>

            <div className="mt-3">
              <div className="text-xs font-semibold mb-1">
                Extra one-off scheduled services
              </div>
              {form.scheduled_services.length > 0 && (
                <ul className="flex flex-col gap-1 mb-2">
                  {form.scheduled_services.map((s, i) => (
                    <li
                      key={i}
                      data-testid={`machine-sched-row-${i}`}
                      className="flex items-center gap-2 text-sm border-2 border-black rounded-lg px-2 py-1 bg-white"
                    >
                      <Calendar size={12} strokeWidth={2.5} />
                      <span className="font-mono-num">{s.date}</span>
                      {s.note && <span className="text-[#607087]">— {s.note}</span>}
                      <button
                        type="button"
                        onClick={() => removeScheduled(i)}
                        className="ml-auto nb-btn bg-white p-1"
                        aria-label="remove"
                      >
                        <Trash2 size={12} strokeWidth={2.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <input
                  type="date"
                  data-testid="machine-sched-date-input"
                  className="nb-input w-44"
                  value={schedDate}
                  onChange={(e) => setSchedDate(e.target.value)}
                />
                <input
                  data-testid="machine-sched-note-input"
                  className="nb-input flex-1 min-w-[160px]"
                  placeholder="optional note (e.g. annual safety check)"
                  value={schedNote}
                  onChange={(e) => setSchedNote(e.target.value)}
                />
                <button
                  type="button"
                  data-testid="machine-sched-add"
                  onClick={addScheduled}
                  disabled={!schedDate}
                  className="nb-btn nb-btn-success px-3 py-2 text-sm flex items-center gap-1"
                >
                  <Plus size={12} strokeWidth={2.5} /> Add date
                </button>
              </div>
            </div>
          </div>

          {/* Linked spares */}
          <div className="sm:col-span-2 border-2 border-black rounded-xl p-3 bg-[#F4F8FB]">
            <div className="label-eyebrow mb-2">Linked spares</div>
            {form.spares.length > 0 && (
              <ul className="flex flex-col gap-1 mb-2">
                {form.spares.map((s, i) => (
                  <li
                    key={i}
                    data-testid={`machine-spare-row-${i}`}
                    className="flex items-center gap-2 text-sm border-2 border-black rounded-lg px-2 py-1 bg-white"
                  >
                    <span
                      className={`label-eyebrow px-1.5 py-0.5 rounded border border-black ${
                        s.kind === "stock" ? "bg-[#DFEEF7]" : "bg-[#F1E7FA]"
                      }`}
                    >
                      {s.kind === "stock" ? "Stock" : "Asset"}
                    </span>
                    <span>{lookupName(s)}</span>
                    <button
                      type="button"
                      onClick={() => removeSpare(i)}
                      className="ml-auto nb-btn bg-white p-1"
                      aria-label="remove"
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <select
                data-testid="machine-spare-kind"
                className="nb-input w-32"
                value={spareKind}
                onChange={(e) => {
                  setSpareKind(e.target.value);
                  setSpareRef("");
                }}
              >
                <option value="stock">Stock item</option>
                <option value="asset">Asset</option>
              </select>
              <select
                data-testid="machine-spare-ref"
                className="nb-input flex-1 min-w-[180px]"
                value={spareRef}
                onChange={(e) => setSpareRef(e.target.value)}
              >
                <option value="">— Pick a {spareKind === "stock" ? "stock item" : "asset"} —</option>
                {sparePool.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
              <input
                data-testid="machine-spare-label"
                className="nb-input w-44"
                value={spareLabel}
                onChange={(e) => setSpareLabel(e.target.value)}
                placeholder="optional label"
              />
              <button
                type="button"
                data-testid="machine-spare-add"
                onClick={addSpare}
                disabled={!spareRef}
                className="nb-btn nb-btn-success px-3 py-2 text-sm flex items-center gap-1"
              >
                <Plus size={12} strokeWidth={2.5} /> Link
              </button>
            </div>
          </div>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="machine-notes-input"
              className="nb-input min-h-[60px]"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Service history caveats, calibration notes, supplier contact…"
            />
          </label>

          {/* Service technicians */}
          <div className="sm:col-span-2 border-2 border-black rounded-xl p-3 bg-[#FFF9EC]">
            <div className="flex items-center gap-2 mb-2">
              <HardHat size={14} strokeWidth={2.5} className="text-[#7A4A00]" />
              <div className="label-eyebrow">Service technicians</div>
            </div>
            {form.tech_links.length > 0 && (
              <ul className="flex flex-col gap-1 mb-2">
                {form.tech_links.map((tl, i) => {
                  const t = technicians.find((x) => x.id === tl.tech_id);
                  return (
                    <li
                      key={tl.tech_id}
                      data-testid={`machine-tech-row-${tl.tech_id}`}
                      className="flex items-center gap-2 text-sm border-2 border-black rounded-lg px-2 py-1.5 bg-white flex-wrap"
                    >
                      <button
                        type="button"
                        data-testid={`machine-tech-primary-${tl.tech_id}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            tech_links: f.tech_links.map((x, j) => ({
                              ...x,
                              is_primary: j === i && !x.is_primary,
                            })),
                          }))
                        }
                        title={tl.is_primary ? "Primary contact" : "Mark as primary"}
                        className={`nb-btn p-1 ${
                          tl.is_primary ? "bg-[#FFB300]" : "bg-white"
                        }`}
                      >
                        <Star
                          size={12}
                          strokeWidth={2.5}
                          fill={tl.is_primary ? "#000" : "none"}
                        />
                      </button>
                      <span className="font-bold flex-1 min-w-0 truncate">
                        {t?.name || tl.tech_name || tl.tech_id}
                        {t?.supplier_name && (
                          <span className="text-[10px] text-[#607087] ml-1">
                            · {t.supplier_name}
                          </span>
                        )}
                      </span>
                      <label
                        className="flex items-center gap-1 text-[10px] text-[#607087]"
                        title="Last time this tech serviced this machine"
                      >
                        Last:
                        <input
                          type="date"
                          data-testid={`machine-tech-last-service-${tl.tech_id}`}
                          className="nb-input py-0.5 px-1 text-xs w-32"
                          value={(tl.last_service_at || "").slice(0, 10)}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              tech_links: f.tech_links.map((x, j) =>
                                j === i
                                  ? { ...x, last_service_at: e.target.value || null }
                                  : x,
                              ),
                            }))
                          }
                        />
                      </label>
                      <button
                        type="button"
                        data-testid={`machine-tech-remove-${tl.tech_id}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            tech_links: f.tech_links.filter((_, j) => j !== i),
                          }))
                        }
                        className="nb-btn bg-white p-1"
                        aria-label="remove"
                      >
                        <Trash2 size={12} strokeWidth={2.5} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <select
                data-testid="machine-tech-add-select"
                className="nb-input flex-1 min-w-[180px]"
                value=""
                onChange={(e) => {
                  const tid = e.target.value;
                  if (!tid) return;
                  const t = technicians.find((x) => x.id === tid);
                  if (!t) return;
                  setForm((f) => {
                    if (f.tech_links.some((x) => x.tech_id === tid)) return f;
                    return {
                      ...f,
                      tech_links: [
                        ...f.tech_links,
                        {
                          tech_id: tid,
                          tech_name: t.name,
                          is_primary: f.tech_links.length === 0,
                          last_service_at: null,
                        },
                      ],
                    };
                  });
                  e.target.value = "";
                }}
              >
                <option value="">— Add a technician… —</option>
                {technicians
                  .filter(
                    (t) => !form.tech_links.some((tl) => tl.tech_id === t.id),
                  )
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.supplier_name ? ` · ${t.supplier_name}` : ""}
                    </option>
                  ))}
              </select>
              {technicians.length === 0 && (
                <div className="text-xs text-[#607087]">
                  Add technicians on the Technicians page first.
                </div>
              )}
            </div>
            {form.tech_links.length > 0 && (
              <div className="text-[10px] text-[#607087] mt-2">
                Tap the star to mark one tech as the primary contact — the
                Machinery card's "Contact tech" button opens their card first.
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Photos</div>
            <MultiImageUpload
              paths={form.image_paths || []}
              onChange={(paths) => set("image_paths", paths)}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-4 border-t-2 border-black flex justify-end gap-2">
          <button onClick={onClose} className="nb-btn bg-white px-4 py-2">
            Cancel
          </button>
          <button
            data-testid="machine-form-save"
            onClick={submit}
            disabled={busy || !form.name.trim()}
            className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1"
          >
            <Save size={14} strokeWidth={2.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MachineFormDialog;
